import * as THREE from "three";
import {
	ENGINE_CLADDING_T_M,
	ENGINE_FURRING_D_M,
	ENGINE_WIND_BARRIER_T_M,
} from "./constants";
import { addWallVent, type VentFacing, type VentKind } from "./detailHelpers";
import { mergeGeometries } from "three/addons/utils/BufferGeometryUtils.js";
import { matVentThroat } from "./materials";
import type {
	SquareBaseBuildContext,
	SquareBaseWall,
	WallFramingResult,
} from "./squareBaseBuilder/context";

export interface VentPassage {
	name: string;
	kind: VentKind;
	position: THREE.Vector3;
	facing: VentFacing;
}
const BORE_HALF_M = 0.054;
const NORMALS: Record<VentFacing, THREE.Vector3> = {
	"+x": new THREE.Vector3(1, 0, 0),
	"-x": new THREE.Vector3(-1, 0, 0),
	"+z": new THREE.Vector3(0, 0, 1),
	"-z": new THREE.Vector3(0, 0, -1),
};
const OPPOSITE: Record<VentFacing, VentFacing> = {
	"+x": "-x",
	"-x": "+x",
	"+z": "-z",
	"-z": "+z",
};

/** Keep the bore in a stud bay and outside door/window openings. */
export function fitVentBetweenStuds(
	ctx: SquareBaseBuildContext,
	walls: Map<SquareBaseWall, WallFramingResult>,
	vent: Pick<VentPassage, "position" | "facing">,
): void {
	const alongX = vent.facing.endsWith("z");
	const wall: SquareBaseWall = alongX
		? vent.facing === "-z"
			? "front"
			: "back"
		: vent.facing === "-x"
			? "right"
			: "left";
	const length = alongX ? ctx.W : ctx.L;
	const axis = alongX ? "x" : "z";
	const desired = vent.position[axis] + length / 2;
	const framing = walls.get(wall)!;
	const clearance = BORE_HALF_M + ctx.sW / 2 + 0.003;
	const y = vent.position.y - ctx.wallBase;
	const blocked = [
		...framing.studAxisPositions.map((p) => ({
			start: p - clearance,
			end: p + clearance,
		})),
		...framing.horizontalMembers
			.filter((m) => y + BORE_HALF_M > m.yStart && y - BORE_HALF_M < m.yEnd)
			.map((m) => ({
				start: m.axisStart - BORE_HALF_M - 0.003,
				end: m.axisEnd + BORE_HALF_M + 0.003,
			})),
		...framing.surfaceOpenings
			.filter(
				(o) =>
					y + BORE_HALF_M > o.yMm / 1000 &&
					y - BORE_HALF_M < (o.yMm + o.heightMm) / 1000,
			)
			.map((o) => ({
				start: o.xMm / 1000 - BORE_HALF_M - 0.003,
				end: (o.xMm + o.widthMm) / 1000 + BORE_HALF_M + 0.003,
			})),
	];
	const candidates = [
		desired,
		...blocked.flatMap((b) => [b.start - 0.001, b.end + 0.001]),
	]
		.filter(
			(p) =>
				p >= ctx.sD + 0.075 &&
				p <= length - ctx.sD - 0.075 &&
				blocked.every((b) => p <= b.start || p >= b.end),
		)
		.sort((a, b) => Math.abs(a - desired) - Math.abs(b - desired));
	if (candidates[0] !== undefined)
		vent.position[axis] = candidates[0] - length / 2;
}

function exteriorPosition(
	ctx: SquareBaseBuildContext,
	passage: VentPassage,
): THREE.Vector3 {
	const normal = NORMALS[passage.facing];
	const axis = normal.x ? "x" : "z";
	const half = normal.x ? ctx.W / 2 : ctx.L / 2;
	const out = passage.position.clone();
	out[axis] =
		-normal[axis] *
		(half +
			ENGINE_WIND_BARRIER_T_M +
			2 * ENGINE_FURRING_D_M +
			ENGINE_CLADDING_T_M);
	return out;
}

/** The cover conceals the square clearance cut; the sleeve itself is round and hollow. */
export function addVentPassageExterior(
	ctx: SquareBaseBuildContext,
	exterior: THREE.Group | null,
	frontExterior: THREE.Group | null,
	interior: THREE.Group | null,
	passage: VentPassage,
): void {
	const outside = exteriorPosition(ctx, passage);
	if (exterior) {
		const target =
			passage.facing === "-z" ? (frontExterior ?? exterior) : exterior;
		const grille = addWallVent({
			group: target,
			position: outside,
			facing: OPPOSITE[passage.facing],
			kind: passage.kind,
			name: `${passage.name}-exterior`,
		});
		const plate = grille.getObjectByName(
			`detail/vent/${passage.name}-exterior/plate`,
		)!;
		grille.remove(plate);
		const slats = Array.from({ length: 4 }, (_, i) =>
			new THREE.BoxGeometry(0.12, 0.012, 0.016).translate(
				0,
				0.036,
				(i - 1.5) * 0.025,
			),
		);
		const louvres = new THREE.Mesh(
			mergeGeometries(slats),
			(grille.children[0] as THREE.Mesh).material,
		);
		louvres.name = `detail/vent/${passage.name}-exterior/louvres`;
		grille.add(louvres);
		for (const slat of slats) slat.dispose();
	}
	if (interior) {
		const valve = interior.getObjectByName(`detail/vent/${passage.name}`);
		if (!valve) return;
		const ring = new THREE.Shape();
		ring.absarc(0, 0, BORE_HALF_M, 0, Math.PI * 2, false);
		const hole = new THREE.Path();
		hole.absarc(0, 0, 0.05, 0, Math.PI * 2, true);
		ring.holes.push(hole);
		const geometry = new THREE.ExtrudeGeometry(ring, {
			depth: outside.distanceTo(passage.position),
			bevelEnabled: false,
			steps: 1,
			curveSegments: 24,
		});
		geometry.rotateX(Math.PI / 2);
		const sleeve = new THREE.Mesh(geometry, matVentThroat);
		sleeve.name = `detail/vent/${passage.name}/sleeve`;
		valve.add(sleeve);
	}
}

type Vertex = Record<string, number[]>;

/** Clip a triangle polygon, interpolating UVs and normals at the cut. */
function clipPolygon(
	vertices: Vertex[],
	axis: number,
	boundary: number,
	greater: boolean,
): Vertex[] {
	const out: Vertex[] = [];
	for (let i = 0; i < vertices.length; i++) {
		const a = vertices[i],
			b = vertices[(i + 1) % vertices.length];
		const da = (a.position[axis] - boundary) * (greater ? 1 : -1),
			db = (b.position[axis] - boundary) * (greater ? 1 : -1);
		if (da >= 0) out.push(a);
		if (da >= 0 !== db >= 0) {
			const t = da / (da - db),
				vertex: Vertex = {};
			for (const key of Object.keys(a))
				vertex[key] = a[key].map((value, j) => value + (b[key][j] - value) * t);
			out.push(vertex);
		}
	}
	return out;
}

/** Cut the surface triangles, including roof-clipped slabs, without changing their UVs.
 * The round sleeve lines the reveal; structural members are never cut here. */
export function cutVentPassages(
	root: THREE.Group,
	ctx: SquareBaseBuildContext,
	passages: readonly VentPassage[],
): void {
	for (const passage of passages) {
		root.updateMatrixWorld(true);
		const outside = exteriorPosition(ctx, passage);
		const bore = new THREE.Box3()
			.setFromPoints([passage.position, outside])
			.expandByScalar(BORE_HALF_M);
		const meshes: THREE.Mesh[] = [];
		const prefixes = [
			"detail/panel/",
			"detail/cladding/",
			"detail/wind-barrier/",
			"detail/furring/",
			"detail/furring-horizontal/",
			"detail/vapor-barrier/",
			"detail/insulation/",
			"detail/interior-batten/",
			"detail/shield/",
		];
		root.traverse((o) => {
			if (
				o instanceof THREE.Mesh &&
				prefixes.some((prefix) => o.name.startsWith(prefix)) &&
				new THREE.Box3().setFromObject(o).intersectsBox(bore)
			)
				meshes.push(o);
		});
		for (const mesh of meshes) {
			const inverse = mesh.matrixWorld.clone().invert();
			const normal = NORMALS[passage.facing]
				.clone()
				.transformDirection(inverse);
			const tangent =
				Math.abs(normal.x) > 0.9 ? 2 : Math.abs(normal.z) > 0.9 ? 0 : null;
			if (tangent === null) continue;
			const cut = bore.clone().applyMatrix4(inverse);
			const axis = tangent === 0 ? "x" : "z";
			const boundaries: [number, number, boolean][] = [
				[tangent, cut.min[axis], true],
				[tangent, cut.max[axis], false],
				[1, cut.min.y, true],
				[1, cut.max.y, false],
			];
			const source = mesh.geometry,
				index = source.index;
			const keys = Object.keys(source.attributes);
			const data: Record<string, number[]> = {};
			for (const key of keys) data[key] = [];
			const count = index?.count ?? source.attributes.position.count;
			for (let offset = 0; offset < count; offset += 3) {
				let remaining: Vertex[] = [0, 1, 2].map((j) => {
					const i = index ? index.getX(offset + j) : offset + j;
					const vertex: Vertex = {};
					for (const key of keys) {
						const attr = source.getAttribute(key);
						vertex[key] = Array.from({ length: attr.itemSize }, (_, k) =>
							attr.getComponent(i, k),
						);
					}
					return vertex;
				});
				const kept: Vertex[][] = [];
				for (const [a, b, greater] of boundaries) {
					if (!remaining.length) break;
					const outsidePart = clipPolygon(remaining, a, b, !greater);
					if (outsidePart.length >= 3) kept.push(outsidePart);
					remaining = clipPolygon(remaining, a, b, greater);
				}
				for (const polygon of kept)
					for (let j = 1; j < polygon.length - 1; j++)
						for (const vertex of [polygon[0], polygon[j], polygon[j + 1]])
							for (const key of keys) data[key].push(...vertex[key]);
			}
			const geometry = new THREE.BufferGeometry();
			for (const key of keys)
				geometry.setAttribute(
					key,
					new THREE.Float32BufferAttribute(
						data[key],
						source.getAttribute(key).itemSize,
					),
				);
			geometry.computeBoundingBox();
			geometry.computeBoundingSphere();
			// Shared engine primitives deliberately make dispose a no-op.
			source.dispose();
			mesh.geometry = geometry;
		}
	}
}
